// src/components/AISection.jsx
import React from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MemoryIcon from "@mui/icons-material/Memory"; 
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import TimelineIcon from "@mui/icons-material/Timeline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import SectionHeader from "./SectionHeader";
import TechTag from "./TechTag";

const features = [
  {
    icon: <QuestionAnswerIcon />,
    title: "Explain complex carbon data",
    body:
      "Turn device logs, token events and certificate records into human-readable summaries for executives, auditors and councils.",
    tags: ["Summarisation", "Plain-language"],
  },
  {
    icon: <PsychologyIcon />,
    title: "Interactive Carbon Compliance Copilot",
    body:
      "Ask questions like “Which sites are at highest risk of non-compliance this quarter?” and get answers backed by on-chain and off-chain data.",
    tags: ["LLM engine", "Risk insights"],
  },
  {
    icon: <TimelineIcon />,
    title: "Scenario modelling & forecasting",
    body:
      "Work with historical measurements and registry data to explore what-if questions around abatement projects and retirement strategies.",
    tags: ["Forecasting", "Scenario analysis"],
  },
  {
    icon: <TaskAltIcon />,
    title: "Guided workflows for admins",
    body:
      "Step through tasks like onboarding a new device, reviewing a tokenisation batch or preparing an audit pack, reducing load on specialist staff.",
    tags: ["Workflows", "Operator support"],
  },
  {
    icon: <DescriptionIcon />,
    title: "Context-aware documentation assistant",
    body:
      "Generate draft reports, registry notes and explanations aligned with your underlying data and methodology, ready for expert review.",
    tags: ["Reporting", "Documentation"],
  },
];

export default function AISection() {
  return (
    <Box
      id="ai-llm"
      sx={{
        py: 10,
        // 🔵 navy / dark background just for this section
        background:
          "radial-gradient(circle at top left, #1f2933, #020617)",
        color: "#e5e7eb", // text inside defaults to light
      }}
    >

      <Container maxWidth="lg">
        <Chip
            label="AI & LLM"
            sx={{
            mb: 1.5,
            bgcolor: "rgba(15,118,110,0.1)",
            color: "#a7f3d0",
            }}
        />

        <SectionHeader
          title="AI & LLM Intelligence Layer"
          subtitle="Large Language Model · Data Integrity · AI Computing"
          kickerColor="#cbd5e1"
          subtitleColor="#cbd5e1"
        />

        <Grid container spacing={6} alignItems="flex-start">
          {/* LEFT – overview copy */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ mb: 2, fontSize: "1.05rem" }}>
              Carbon Bridge Computing includes an AI-powered intelligence layer
              that turns raw registry data into decisions you can act on.
            </Typography>
            <Typography sx={{ mb: 2, fontSize: "1.05rem" }}>
              Our large-language-model (LLM) engine is trained on energy, carbon
              and regulatory concepts so it can sit on top of your devices,
              tokens and certificates as a digital copilot, not just a chatbot.
            </Typography>

            {/*<div className="neon-divider" />*/}            
            
            <Typography sx={{ mb: 0.5, fontWeight: 600 }}>
              What it does
            </Typography>
            <Typography sx={{ fontSize: "1.05rem", color: "rgba(226,232,240,0.9)" }}>
              By combining verifiable infrastructure with an AI assistant that
              understands your carbon data model, Carbon Bridge Computing makes
              registry operations, reporting and decision-making faster, safer
              and easier to explain to every stakeholder.
            </Typography>
          </Grid>

          {/* RIGHT – feature cards */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {features.map((f) => (
                <Grid item xs={12} key={f.title}>
                  <Card
                    className="glass-card glow"
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(148,163,184,0.45)",
                      // darker paper over navy background
                      background:
                        "linear-gradient(135deg, #020617, #0b1120)",
                    }}
                  >
                    <CardContent sx={{ pb: 2.5 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "999px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "radial-gradient(circle, #3b82f6, #6366f1)",
                            color: "#fff",
                            flexShrink: 0,
                          }}
                        >
                          {f.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 0.5, color: "#f9fafb" }}
                          >
                            {f.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, color: "rgba(209,213,219,0.9)" }}
                          >
                            {f.body}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {f.tags.map((t) => (
                              <TechTag key={t} label={t} />
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
